import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Alert,
  CircularProgress,
  InputLabel
} from '@mui/material';
import { InvestmentProfile } from '../../types/investment';
import { investmentAPI } from '../../api/investmentAPI';
import { useAuth } from '../../contexts/AuthContext';
import { useLoadingStatus } from '../../contexts/LoadingStatusContext';
import { useMemo } from 'react';

interface Props {
  onProfileCreated: (profile: InvestmentProfile) => void;
  open: boolean;
  onClose: () => void;
}

export const CreateProfileDialog: React.FC<Props> = ({
  onProfileCreated,
  open,
  onClose
}) => {
  const [name, setName] = useState('');
  const [options, setOptions] = useState<any>(null);
  const [selectedOptions, setSelectedOptions] = useState({
    investment_profile_template: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Use auth context
  const { addStatus, removeStatus } = useLoadingStatus();
  const api = useMemo(() => investmentAPI(addStatus, removeStatus), []);

  useEffect(() => {
    const fetchOptions = async () => {
      setLoading(true);
      try {
        const data = await api.getProfileOptions();
        setOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        setError('Failed to load profile options');
      } finally {
        setLoading(false);
      }
    };
    if (open) {
      fetchOptions();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setName('');
      setSelectedOptions({ investment_profile_template: '' });
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!name || !selectedOptions.investment_profile_template) return;

    try {
      setLoading(true);
      setError(null);
      
      const newProfile = await api.createProfile({
        name,
        investment_profile_template: selectedOptions.investment_profile_template
      });

      onProfileCreated(newProfile);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>Create Investment Profile</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {loading && <CircularProgress size={24} sx={{ position: 'absolute', top: '50%', left: '50%', mt: -1.5, ml: -1.5 }} />}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <TextField
          fullWidth
          label="Profile Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 2 }}
          disabled={loading}
          required
          autoFocus
          margin="normal"
        />

        {options && (
          <FormControl fullWidth required margin="normal">
            <InputLabel id="investment-profile-template-label">Investment Profile Template</InputLabel>
            <Select
              labelId="investment-profile-template-label"
              label="Investment Profile Template"
              value={selectedOptions.investment_profile_template}
              onChange={(e) => setSelectedOptions({
                investment_profile_template: e.target.value as string
              })}
              disabled={loading}
            >
              <MenuItem value="" disabled>Select Template</MenuItem>
              {options.map((ipt: { name: string; description: string; }) => (
                <MenuItem key={ipt.name} value={ipt.name}>
                  {ipt.name} {ipt.description ? `- ${ipt.description}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !name || !selectedOptions.investment_profile_template}
          color="primary"
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
